import { UTCDeliveryPeriod } from '@epicuro-next/ataraxia/models';
import { createReducer, on } from '@ngrx/store';

import * as actions from '../actions';
import { DisplayByMode, UiState } from '../models/config-strip-state.models';
import { formatDisplayByMode } from '../shared/shared';

import {
  preselectDeliveryPeriodsUi,
  preSelectNowDeliveryPeriodUi,
} from './delivery-periods';
import { addBulkDeliveryPeriodUi } from './delivery-periods/bulk-delivery-periods/add-bulk-delivery-period.reducer';
import { preSelectBulkDeliveryPeriodUi } from './delivery-periods/bulk-delivery-periods/preselect-bulk-delivery-period.reducer';
import { selectBulkDeliveryPeriodUi } from './delivery-periods/bulk-delivery-periods/select-bulk-delivery-period.reducer';
import { copyToDeliveryPeriodUi } from './delivery-periods/copy-to-delivery-period';
import { addFutureDeliveryPeriodUi } from './delivery-periods/future-delivery-periods/deprecated/add-future-delivery-period.reducer';
import { preSelectFutureDeliveryPeriodUi } from './delivery-periods/future-delivery-periods/preselect-future-delivery-period.reducer';
import { selectFutureDeliveryPeriodUi } from './delivery-periods/future-delivery-periods/select-future-delivery-period.reducer';
import { selectNowDeliveryPeriodUi } from './delivery-periods/now-delivery-periods/select-now-delivery-periods.reducer';
import { preSelectAnyDeliveryPeriodUi } from './delivery-periods/preselect-delivery-periods/preselect-any-delivery-periods.reducer';

export const uiInitialState: UiState = {
  isOpen: false,
  loading: false,
  error: null,
  pricesLoading: false,
  pricesError: null,
  validating: false,
  validationErrors: {},
  initialSelectedVariant: null,
  selectedVariant: null,
  initialSelectedPrepack: null,
  selectedPrepack: null,
  selectedSize: null,
  preSelectDisplayByMode: null,
  preSelectedOrderType: null,
  selectedOrderType: null,
  preSelectedDeliveryPeriod: null,
  preSelectedDeliveryPeriodType: null,
  displayByMode: DisplayByMode.Size,
  pastedData: false,
  copiedDeliveryPeriods: [],
  copiedShops: [],
  loadedCached: false,
  ecommerceTrackingSource: null,
};

export const uiConfigStripReducer = createReducer<UiState>(
  uiInitialState,
  on(actions.setConfigStripIsOpen, (state: UiState, { open }) => ({
    ...state,
    isOpen: open,
  })),
  on(actions.setProductQuantity, actions.setProductQuantities, (state) => ({
    ...state,
    copiedShops: [],
    copiedDeliveryPeriods: [],
  })),
  on(actions.loadProductFailure, (state, { error }) => ({
    ...state,
    loading: false,
    pricesLoading: false,
    error,
  })),
  on(actions.loadProduct, (state) => ({
    ...uiInitialState,
    loading: true,
    pricesLoading: true,
    error: null,
    isOpen: state.isOpen,
    preSelectedOrderType: state.preSelectedOrderType,
    preSelectDisplayByMode: state.preSelectDisplayByMode,
    preSelectedDeliveryPeriod: state.preSelectedDeliveryPeriod,
    preSelectedDeliveryPeriodType: state.preSelectedDeliveryPeriodType,
    ecommerceTrackingSource: state.ecommerceTrackingSource,
  })),
  on(actions.cachedProductResolved, (state) => ({
    ...state,
    loading: false,
    loadedCached: true,
  })),
  on(actions.loadProductSuccess, (state, { product }) => ({
    ...state,
    loading: false,
    pricesLoading: false,
    loadedCached: false,
    error: null,
    initialSelectedPrepack: product.prepacks?.[0]?.productId ?? null,
    selectedPrepack: state.selectedPrepack ?? state.initialSelectedPrepack,
    selectedOrderType: state.preSelectedOrderType,
    selectedVariant: state.selectedVariant ?? state.initialSelectedVariant,
    selectedSize: null,
    displayByMode: formatDisplayByMode(
      state.preSelectDisplayByMode,
      product.isLocked,
    ),
    preSelectDisplayByMode: null,
  })),
  on(actions.selectVariant, (state, { productId }) => ({
    ...state,
    initialSelectedVariant: state.initialSelectedVariant ?? productId,
    selectedVariant: productId,
  })),
  on(actions.selectSize, (state, { sizeId, variantId }) => ({
    ...state,
    selectedVariant: variantId,
    selectedSize: sizeId,
  })),
  on(actions.selectPrepack, (state, { productId }) => ({
    ...state,
    selectedPrepack: productId,
  })),
  on(actions.preSelectDisplayByMode, (state, { preSelectDisplayByMode }) => ({
    ...state,
    preSelectDisplayByMode,
  })),
  on(actions.setDisplayByMode, (state, { displayByMode }) => ({
    ...state,
    displayByMode,
  })),
  on(actions.setActiveDeliveryShop, (state) => ({
    ...state,
    copiedDeliveryPeriods: [],
  })),
  on(actions.clearState, () => ({
    ...uiInitialState,
  })),
  on(actions.persistChangesSuccess, (state) => ({
    ...state,
    error: null,
  })),
  on(actions.persistChangesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(actions.loadPrices, (state) => ({
    ...state,
    pricesLoading: true,
  })),
  on(actions.loadPricesFailure, (state, { error }) => ({
    ...state,
    pricesError: error,
    pricesLoading: false,
  })),
  on(actions.loadPricesSuccess, (state) => ({
    ...state,
    pricesError: null,
    pricesLoading: false,
  })),
  on(actions.resetPasteStatus, (state) => ({
    ...state,
    pastedData: false,
  })),
  on(actions.validateDeliveryItems, (state, { validating }) => ({
    ...state,
    validating,
  })),
  on(
    actions.validateDeliveryItemsSuccess,
    (state, { validationErrors, deliveryPeriod, deliveryShop }) => ({
      ...state,
      validating: false,
      validationErrors: {
        ...state.validationErrors,
        [deliveryShop]: {
          ...state.validationErrors?.[deliveryShop],
          [deliveryPeriod]: validationErrors,
        },
      },
    }),
  ),
  /** add FUTURE delivery period, it will be cached */
  on(
    actions.addDeliveryPeriod,
    (
      state: UiState,
      {
        deliveryPeriod,
      }: {
        deliveryPeriod: UTCDeliveryPeriod & actions.isTrustedDeliveryPeriod;
      },
    ) => addFutureDeliveryPeriodUi(state, deliveryPeriod),
  ),
  /** add FUTURE delivery period, it will not be cached, because it is not created by user */
  on(
    actions.addAutoDeliveryPeriod,
    (
      state: UiState,
      {
        deliveryPeriod,
      }: {
        deliveryPeriod: UTCDeliveryPeriod & actions.isTrustedDeliveryPeriod;
      },
    ) => addFutureDeliveryPeriodUi(state, deliveryPeriod),
  ),
  /** select FUTURE delivery period */
  on(actions.selectFutureDeliveryPeriod, selectFutureDeliveryPeriodUi),
  /** pre-select FUTURE delivery period */
  on(actions.preSelectFutureDeliveryPeriod, preSelectFutureDeliveryPeriodUi),
  /** add BULK delivery period */
  on(actions.addBulkDeliveryPeriod, addBulkDeliveryPeriodUi),
  /** select BULK delivery period */
  on(actions.selectBulkDeliveryPeriod, selectBulkDeliveryPeriodUi),
  /** pre-select BULK delivery period */
  on(actions.preSelectBulkDeliveryPeriod, preSelectBulkDeliveryPeriodUi),
  /** select NOW delivery period */
  on(actions.selectNowDeliveryPeriod, selectNowDeliveryPeriodUi),
  /** pre-select NOW delivery period */
  on(actions.preSelectNowDeliveryPeriod, preSelectNowDeliveryPeriodUi),
  on(actions.preSelectAnyDeliveryPeriod, preSelectAnyDeliveryPeriodUi),
  /** copies and nullable from preselectedType to shop's delivery period selection */
  on(actions.setPreselectedDeliveryPeriod, preselectDeliveryPeriodsUi),
  on(actions.copyToShops, (state, { toShopIds }) => {
    return {
      ...state,
      pastedData: true,
      copiedShops: [...state.copiedShops, ...toShopIds],
    };
  }),
  on(actions.clearShopQuantities, (state, { shopId }) => ({
    ...state,
    copiedShops: state.copiedShops.filter((id) => id !== shopId),
  })),
  on(actions.copyToDeliveryPeriod, copyToDeliveryPeriodUi),
  on(actions.goToNextProduct, (state) => ({
    ...state,
    loading: true,
  })),
  on(actions.setEcommerceTrackingSource, (state, { data }) => {
    return {
      ...state,
      ecommerceTrackingSource: data,
    };
  }),
);
